/**
 * People-flow (agente de onboarding de People) — contratos compartidos entre
 * el router peopleFlow y el Panel de Control People.
 */

export type PeopleFlowStage = 'pre_start' | 'day_one' | 'week_one' | 'global' | 'day_thirty';

export const PEOPLE_FLOW_STAGE_ORDER: PeopleFlowStage[] = [
  'pre_start',
  'day_one',
  'week_one',
  'global',
  'day_thirty',
];

export type PeopleFlowStepId =
  | 'validation'
  | 'cal_day1'
  | 'cal_leader'
  | 'rep_reminder'
  | 'leader_notice'
  | 'day1_dm'
  | 'channels_dm'
  | 'learning_dm'
  | 'whatsapp_dm'
  | 'week1_form_dm'
  | 'global_dm'
  | 'global_followup_dm'
  | 'checkin30_dm';

export type PeopleFlowStepStatus =
  | 'ok'
  /** Corrió, pero dejó trabajo humano pendiente (ver `pending`). */
  | 'ok_pending'
  | 'scheduled'
  | 'waiting'
  | 'error'
  | 'expired'
  | 'blocked_validation'
  | 'na';

export interface PeopleFlowStep {
  stepId: PeopleFlowStepId;
  stage: PeopleFlowStage;
  channel: 'slack' | 'mail' | 'calendar' | 'internal';
  /** Paso del doc funcional de People (ej. "0.4"). */
  docStep: string;
  status: PeopleFlowStepStatus;
  dueAt: string | null;
  executedAt: string | null;
  error: string | null;
  /** Valor crudo del flag (ej. 'creado_sin_sala', 'mail_fallback'). */
  raw: string | null;
  /** Qué queda por hacer a mano cuando el status es 'ok_pending'. */
  pending: string | null;
}

export interface PeopleFlowAltaSummary {
  idAlta: string;
  nombre: string;
  correoPersonal: string;
  correoWinclap: string;
  fechaIngreso: string;
  /** ISO o null si la fecha no parsea. */
  fechaIngresoParsed: string | null;
  pais: string;
  comunidad: string;
  rol: string;
  impactLevel: string;
  team: string;
  area: string;
  managerEmail: string;
  recruiterEmail: string;
  officeManager: string;
}

export interface PeopleFlowAltaStatus {
  alta: PeopleFlowAltaSummary;
  validation: { complete: boolean; missing: string[]; missingLabels: string[] };
  steps: PeopleFlowStep[];
  /** Link directo a la planilla Altas 2025 (para "Abrir planilla ↗"). */
  sheetUrl: string;
}

export interface PeopleFlowAuditEntry {
  actionId: string;
  altaId: string;
  createdAt: string;
  action: string;
  stepId?: string;
  detail?: string;
  performedBy: string;
  summary?: Record<string, unknown>;
}

export interface PeopleFlowRunSummary {
  createdAt: string;
  kind: 'people_flow' | 'daily';
  performedBy: string;
  summary: Record<string, unknown>;
}

/**
 * Compu del ingreso (columna "Compu" del Panel People, pedido KP 14/09): la que
 * ya tiene asignada en el inventario, o las disponibles en su clapbase para
 * asignarle desde el panel. Misma regla de "disponible" que Aprobaciones.
 */
export interface PeopleFlowEquipment {
  idAlta: string;
  officeLabel: string;
  /** false ⇒ sin oficina: la compu se envía a domicilio. */
  hasOffice: boolean;
  assigned: { modelo: string; nroSerie: string } | null;
  /** Disponibles para asignar ya (Lista para Entregar + sin owner @winclap). */
  available: number;
  /** Total activo en la clapbase sin entregar (incluye en preparación). */
  total: number;
  devices: Array<{
    deviceId: string;
    modelo: string;
    nroSerie: string;
    status: string;
    owner: string;
    available: boolean;
  }>;
}
